import { Component, Input, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy, signal, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';

// Register Swiper Web Components
import { register } from 'swiper/element/bundle';
register();

@Component({
  selector: 'app-flipbook',
  standalone: true,
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './flipbook.component.html',
  styleUrl: './flipbook.component.scss'
})
export class FlipbookComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() pdfUrl!: string;
  @ViewChild('swiperContainer') swiperContainer!: ElementRef;

  pdfDoc: any = null;
  pages = signal<number[]>([]);
  loading = signal(true);
  currentPage = signal(1);
  errorMsg = signal<string | null>(null);
  renderedPages = new Set<number>();

  ngOnInit() {
  }

  async loadPdfJs() {
    return new Promise((resolve) => {
      if ((window as any).pdfjsLib) {
        resolve((window as any).pdfjsLib);
        return;
      }
      const script = document.createElement('script');
      script.src = '/assets/pdfjs/pdf.min.js';
      script.onload = () => {
        (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';
        resolve((window as any).pdfjsLib);
      };
      script.onerror = (err) => {
        console.error('Failed to load local PDF.js script', err);
        resolve(null);
      };
      document.body.appendChild(script);
    });
  }

  async ngAfterViewInit() {
    try {
      if (!this.pdfUrl) {
        this.errorMsg.set('PDF URL is empty');
        this.loading.set(false);
        return;
      }
      const pdfjsLib: any = await this.loadPdfJs();
      if (!pdfjsLib) {
        throw new Error('Failed to load local PDF.js. Please RESTART your Angular server so it can serve the new /assets/ files.');
      }
      this.pdfDoc = await pdfjsLib.getDocument({ 
        url: this.pdfUrl,
        cMapUrl: '/assets/pdfjs/cmaps/',
        cMapPacked: true,
        standardFontDataUrl: '/assets/pdfjs/standard_fonts/',
        disableFontFace: true, // Forces PDF.js to draw fonts as paths instead of relying on the browser's Font API!
        fontExtraProperties: true
      }).promise;
      const numPages = this.pdfDoc.numPages;
      this.pages.set(Array.from({ length: numPages }, (_, i) => i + 1));
      
      setTimeout(() => {
        try {
          this.initSwiper();
        } catch(e: any) {
          console.error("Swiper init error:", e);
          this.errorMsg.set('Failed to initialize Swiper: ' + e.message);
          this.loading.set(false);
        }
      }, 300);
    } catch (err: any) {
      console.error('Error loading PDF:', err);
      this.errorMsg.set(err?.message || 'Unknown error loading PDF');
      this.loading.set(false);
    }
  }

  initSwiper() {
    const swiperEl = this.swiperContainer.nativeElement;
    
    // Assign Swiper parameters programmatically
    const swiperParams = {
      grabCursor: true,
      centeredSlides: true,
      slidesPerView: 'auto',
      spaceBetween: 38, // Figma gap between cards
      dir: 'rtl',
      keyboard: {
        enabled: true,
      },
      zoom: {
        maxRatio: 3,
        minRatio: 1,
      },
      on: {
        slideChange: () => {
          if (swiperEl.swiper) {
            const activeIndex = swiperEl.swiper.realIndex;
            const newPage = activeIndex + 1; // Swiper is 0-indexed
            this.currentPage.set(newPage);
            this.renderSurroundingPages(newPage);
          }
        },
      },
    };

    Object.assign(swiperEl, swiperParams);
    swiperEl.initialize();
    
    // Render initial pages
    this.renderSurroundingPages(1);
    this.loading.set(false);
  }

  async renderSurroundingPages(centerPage: number) {
    // We want to render current page, and a few ahead and behind
    const pagesToRender = [
      centerPage - 2, centerPage - 1, centerPage, centerPage + 1, centerPage + 2, centerPage + 3
    ];

    for (const pageNum of pagesToRender) {
      if (pageNum >= 1 && pageNum <= this.pages().length) {
        if (!this.renderedPages.has(pageNum)) {
          this.renderPage(pageNum);
          this.renderedPages.add(pageNum);
        }
      }
    }
  }

  async renderPage(pageNum: number) {
    try {
      const page = await this.pdfDoc.getPage(pageNum);
      const canvas = document.getElementById(`page-canvas-${pageNum}`) as HTMLCanvasElement;
      if (!canvas) return;

      // Increase scale to 3.0 to prevent canvas text rounding errors which cause overlap
      const viewport = page.getViewport({ scale: 3.0 });
      const context = canvas.getContext('2d', { alpha: false }); // alpha: false can improve font rendering
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;
      
      // Convert the rendered canvas into a static image
      const img = document.getElementById(`page-img-${pageNum}`) as HTMLImageElement;
      if (img) {
        img.src = canvas.toDataURL('image/jpeg', 0.85); // High quality JPEG
      }
    } catch (err) {
      console.error(`Error rendering page ${pageNum}:`, err);
    }
  }

  nextPage() {
    if (this.swiperContainer?.nativeElement?.swiper) {
      this.swiperContainer.nativeElement.swiper.slideNext();
    }
  }

  prevPage() {
    if (this.swiperContainer?.nativeElement?.swiper) {
      this.swiperContainer.nativeElement.swiper.slidePrev();
    }
  }
  
  zoomIn() {
    if (this.swiperContainer?.nativeElement?.swiper?.zoom) {
      this.swiperContainer.nativeElement.swiper.zoom.in();
    }
  }

  zoomOut() {
    if (this.swiperContainer?.nativeElement?.swiper?.zoom) {
      this.swiperContainer.nativeElement.swiper.zoom.out();
    }
  }
  
  goToPage(pageNum: number) {
    if (this.swiperContainer?.nativeElement?.swiper) {
      this.swiperContainer.nativeElement.swiper.slideTo(pageNum - 1);
    }
  }

  getTimelinePages(): number[] {
    const current = this.currentPage();
    const total = this.pages().length;
    
    if (total === 0) return [];
    if (total === 1) return [1];
    if (total === 2) return [2, 1];
    
    // RTL order: smaller numbers first (renders on the right in RTL)
    if (current === 1) return [1, 2, 3];
    if (current === total) return [total - 2, total - 1, total];
    return [current - 1, current, current + 1];
  }

  ngOnDestroy() {
    // Swiper cleans itself up if we destroy the element
  }
}
